import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import { cn } from '../../utils/cn';
import { contactApi, settingsApi } from '../../services/api';

interface ContactInfoItem {
  icon: string;
  title: { zh: string; vi: string };
  content: { zh: string; vi: string };
}

const ContactSection: React.FC = () => {
  const { language, t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [contactInfo, setContactInfo] = useState<ContactInfoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [siteSettings, setSiteSettings] = useState<Record<string, string>>({});

  // 从API获取联系方式数据
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 并行获取网站设置和联系方式
        const [settingsRes, contactRes] = await Promise.all([
          settingsApi.getWebsiteSettings().catch(() => ({ success: false, data: {} })),
          contactApi.getContactInfos('').catch(() => ({ success: false, data: { contactInfos: [] } }))
        ]);
        
        if (settingsRes.success && settingsRes.data) {
          setSiteSettings(settingsRes.data);
        }
        
        if (contactRes.success && contactRes.data?.contactInfos?.length > 0) {
          // 将API数据转换为组件需要的格式
          const formattedData = contactRes.data.contactInfos.map((info: any) => {
            let icon = '📍';
            if (info.type === 'address' || info.type === 'location') {
              icon = '📍';
            } else if (info.type === 'phone') {
              icon = '📞';
            } else if (info.type === 'email') {
              icon = '✉️';
            } else if (info.type === 'working_hours' || info.type?.includes('time')) {
              icon = '⏰';
            }
            
            const titleZh = info.label_zh || info.name_zh || info.type;
            const titleVi = info.label_vi || info.name_vi || info.type;
            const contentZh = info.value_zh || info.value || '';
            const contentVi = info.value_vi || info.value || '';
            
            return { icon, title: { zh: titleZh, vi: titleVi }, content: { zh: contentZh, vi: contentVi } };
          });
          
          setContactInfo(formattedData);
        } else {
          // 使用网站设置作为后备
          setContactInfo(getDefaultContactInfo(settingsRes.success ? settingsRes.data : {}));
        }
        setError(null);
      } catch (err) {
        console.error('获取联系方式失败:', err);
        setError('无法加载联系方式信息');
        setContactInfo(getDefaultContactInfo({}));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 默认联系方式数据（从网站设置读取）
  const getDefaultContactInfo = (settings: Record<string, string>): ContactInfoItem[] => {
    return [
      {
        icon: '📍',
        title: {
          zh: '公司地址',
          vi: 'Địa chỉ công ty'
        },
        content: {
          zh: settings.company_address_zh || '越南北宁省',
          vi: settings.company_address_vi || 'Bắc Ninh, Việt Nam'
        }
      },
      {
        icon: '📞',
        title: {
          zh: '联系电话',
          vi: 'Điện thoại liên hệ'
        },
        content: {
          zh: settings.contact_phone || '+84 987 654 321',
          vi: settings.contact_phone || '+84 987 654 321'
        }
      },
      {
        icon: '✉️',
        title: {
          zh: '电子邮箱',
          vi: 'Email'
        },
        content: {
          zh: settings.contact_email || 'info@huiya.com.vn',
          vi: settings.contact_email || 'info@huiya.com.vn'
        }
      },
      {
        icon: '⏰',
        title: {
          zh: '工作时间',
          vi: 'Giờ làm việc'
        },
        content: {
          zh: settings.working_hours_zh || '周一至周六 8:00-17:30',
          vi: settings.working_hours_vi || 'Thứ 2-6: 8:00-17:30'
        }
      }
    ];
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // 模拟表单提交
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
      
      // 3秒后重置状态
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 3000);
    }, 1500);
  };



  return (
    <section id="contact" className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {language === 'zh' ? '联系我们' : 'Liên hệ chúng tôi'}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'zh'
              ? '欢迎咨询建筑工程相关业务，我们将尽快为您提供专业的解决方案'
              : 'Chào mừng tư vấn về dịch vụ xây dựng công trình, chúng tôi sẽ cung cấp giải pháp chuyên nghiệp cho bạn trong thời gian sớm nhất'
            }
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* 联系信息 */}
          <div>
            <div className="bg-white rounded-2xl p-4 md:p-8 shadow-lg">
              <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4 md:mb-6">
                {language === 'zh' ? '联系信息' : 'Thông tin liên hệ'}
              </h3>
              
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="text-2xl">{info.icon}</div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-1">
                        {info.title[language]}
                      </h4>
                      <p className="text-gray-600">{info.content[language]}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <h4 className="text-lg font-medium text-gray-900 mb-4">
                  {language === 'zh' ? '服务范围' : 'Phạm vi dịch vụ'}
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { zh: '电气工程', vi: 'Công trình điện' },
                    { zh: '暖通空调', vi: 'Hệ thống HVAC' },
                    { zh: '通风系统', vi: 'Hệ thống thông gió' },
                    { zh: '建筑装饰', vi: 'Trang trí công trình' },
                    { zh: '改造工程', vi: 'Công trình cải tạo' },
                    { zh: '防水地坪漆', vi: 'Sơn sàn chống thấm' }
                  ].map((service, index) => (
                    <div 
                      key={index}
                      className="bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm font-medium"
                    >
                      {service[language]}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 联系表单 */}
          <div>
            <div className="bg-white rounded-2xl p-4 md:p-8 shadow-lg">
              <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-4 md:mb-6">
                {language === 'zh' ? '发送消息' : 'Gửi tin nhắn'}
              </h3>

              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder={language === 'zh' ? '请输入您的姓名' : 'Vui lòng nhập tên của bạn'}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('email')}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="example@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        {t('phone')}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="+84 123 456 789"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('message')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      placeholder={language === 'zh' ? '请描述您的项目需求...' : 'Vui lòng mô tả nhu cầu dự án của bạn...'}
                    />
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                      {language === 'zh'
                        ? '消息已发送成功！我们会尽快与您联系。'
                        : 'Tin nhắn đã được gửi thành công! Chúng tôi sẽ liên hệ với bạn sớm nhất.'
                      }
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={cn(
                      "w-full py-3 px-6 rounded-lg font-medium text-white transition-all",
                      isSubmitting
                        ? "bg-blue-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700 active:transform active:scale-95"
                    )}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {language === 'zh' ? '发送中...' : 'Đang gửi...'}
                      </span>
                    ) : (
                      t('sendMessage')
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;